import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import React, { useEffect, useState } from 'react';
import './setup.css';
import TabButton from 'components/Button/TabButton/TabButton';
import { ESetupTabOptions, sideTabs } from 'enums/ESetupTabOptions';
import SetupDashboard from '../SetupDashboard';
import CrystalContainerDashboard from '../CrystalContainer';
import { useParams, useNavigate } from 'react-router-dom';
import { setSetupTab } from 'redux/reducers/setupReducer';
import { useRMDispatch } from 'redux/hooks';
import { useSetupTabOption } from 'redux/hooks/SetupTab';
import ImagingDashboard from '../Imaging';
import PlateTypesDashboard from '../PlateTypes';
import Ingredients from 'pages/ingredients/Ingredients';
import RMModal, { RMModalProps } from 'components/modal/RMModal';
import DispensingPropertiesDashboard from '../DispensingProperties';
import { useLicenseAuthorizeCommand } from 'CustomHooks';
import { EUserAction } from 'constants/types';
import { ArchivingDashboard } from 'components/archiving';

export const SetupTabs = () => {
  const { name } = useParams();
  const setupTab = useSetupTabOption();
  const [tabIndex, setTabIndex] = useState<number>(ESetupTabOptions.General);
  const [modal, setModal] = useState<RMModalProps>();
  const navigate = useNavigate();
  const dispatch = useRMDispatch();
  const licenseAuthCommand = useLicenseAuthorizeCommand();

  useEffect(() => {
    dispatch(setSetupTab(name ?? sideTabs[ESetupTabOptions.General].value));
    setTabIndex(
      sideTabs.find((tab) => tab.value === name)?.key ?? ESetupTabOptions.General,
    );
  }, [name]);

  const changeTab = async (index: number) => {
    dispatch(setSetupTab(sideTabs[index].value));
    navigate(`/setup/${sideTabs[index].value}`);
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, showModal: false }));
  };

  return (
    <>
      <Tabs
        id="setuptabs-main-container"
        className="setup-tab"
        selectedIndex={tabIndex}
        onSelect={(index) => {
          if (index === tabIndex) {
            return false;
          }

          const navigate = () => {
            changeTab(index);
          };

          if (index === ESetupTabOptions.Imaging) {
            licenseAuthCommand(navigate, EUserAction.Imaging)();
          } else if (index === ESetupTabOptions.CrystalContainer) {
            licenseAuthCommand(navigate, EUserAction.SynchrotronTrip)();
          } else {
            navigate();
          }

          // Prevent react-tabs from managing its own internal state;
          // we fully control the active tab via `selectedIndex`.
          return false;
        }}
      >
        <TabList id="setuptabs-lists-wrapper" className="setup-tab-list">
          <Tab>
            <TabButton
              titleTip={'General'}
              active={setupTab === sideTabs[ESetupTabOptions.General].value}
              IconProps={{ name: 'Globe', subfolder: 'Setup' }}
            />
          </Tab>
          <Tab>
            <TabButton
              titleTip={'Ingredients'}
              active={setupTab === sideTabs[ESetupTabOptions.Ingredients].value}
              IconProps={{ name: 'Reagents', subfolder: 'Setup' }}
            />
          </Tab>
          <Tab>
            <TabButton
              titleTip={'Plate Types'}
              active={setupTab === sideTabs[ESetupTabOptions.PlateTypes].value}
              IconProps={{ name: 'Plate', subfolder: 'Setup' }}
            />
          </Tab>
          <Tab>
            <TabButton
              titleTip={'Dispensing Properties'}
              active={setupTab === sideTabs[ESetupTabOptions.DispenseProperties].value}
              IconProps={{ name: 'Dispense', subfolder: 'Setup' }}
            />
          </Tab>
          <Tab>
            <TabButton
              titleTip={'Imaging'}
              active={setupTab === sideTabs[ESetupTabOptions.Imaging].value}
              IconProps={{ name: 'Camera', subfolder: 'Setup' }}
            />
          </Tab>
          <Tab>
            <TabButton
              titleTip={'Archiving'}
              active={setupTab === sideTabs[ESetupTabOptions.Archiving].value}
              IconProps={{ name: 'Archiving' }}
            />
          </Tab>
          <Tab>
            <TabButton
              titleTip={'Crystal Container'}
              active={setupTab === sideTabs[ESetupTabOptions.CrystalContainer].value}
              IconProps={{ name: 'xtal', subfolder: 'Setup' }}
            />
          </Tab>
        </TabList>
        <div
          id="setuptabs-panels-wrapper"
          className="flex w-full h-full flex-column content-width"
        >
          <TabPanel>
            <SetupDashboard />
          </TabPanel>
          <TabPanel>
            <Ingredients />
          </TabPanel>
          <TabPanel>
            <PlateTypesDashboard />
          </TabPanel>
          <TabPanel>
            <DispensingPropertiesDashboard />
          </TabPanel>
          <TabPanel>
            <ImagingDashboard setModal={setModal} />
          </TabPanel>
          <TabPanel className="w-full h-full">
            <ArchivingDashboard />
          </TabPanel>
          <TabPanel className="w-full h-full">
            <CrystalContainerDashboard />
          </TabPanel>
        </div>
      </Tabs>
      <RMModal showModal={modal?.showModal} onOk={closeModal} {...modal} />
    </>
  );
};
